/**
 * What students need.
 *
 * Four distributions, every one of them exact, whole-population and drawn from
 * a field that sits on every student record, and not one of them is on any P3
 * surface today. For a VP of Student Success this is more immediately useful
 * than any outcome metric, because it is actionable this week: it says what
 * this cohort is asking for, where they want to go, and how much mentor time
 * they expect.
 *
 * ── WHY A STACKED BAR IS NOT USED FOR SUPPORT AREAS ──────────────────────
 * A student can select more than one support area, so the rows sum to
 * selections and not to students. Drawing them as parts of one whole would
 * assert a share of a population that does not exist. Ranked bars, with the
 * card subtitle saying so.
 *
 * ── WHEN EVERY GROUP IS SMALL ────────────────────────────────────────────
 * The whole section collapses to one state card rather than four suppressed
 * ones. Four cards that each say "not shown" reads as a broken product; one
 * card that states the rule, the current position and when it turns on reads
 * as a measurement that has not started.
 */

import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  ProvenanceNote,
  SectionHead,
  StateCard,
} from "~/app/components/primitives";
import { SECTION_LEDE, SECTION_TITLE } from "~/app/lib/admin-nav";
import type { CohortView, Distribution } from "~/app/lib/demo-data";
import { RankedList, SERIES } from "~/app/panels/panel-charts";

function DistributionCard({
  distribution,
  title,
  sub,
  span,
  solo,
  color,
  contextLabel,
}: {
  distribution: Distribution;
  title: string;
  sub: string;
  span?: 1 | 2;
  solo?: boolean;
  color: string;
  contextLabel: string;
}) {
  return (
    <Card span={span} solo={solo}>
      <CardHeader>
        <CardTitle sub={sub}>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {distribution.allSuppressed ? (
          <StateCard variant="building" title="No group here has reached ten">
            Every value in this breakdown covers fewer than ten students on its
            own, so none is published. It fills in as the cohort grows.
          </StateCard>
        ) : (
          <RankedList
            distribution={distribution}
            label={contextLabel}
            color={color}
          />
        )}
      </CardBody>
      {/* The roster standard (Thomas, Sep 9 2026): the footer is the
          provenance line and the legend, nothing else. The distribution's own
          footnote repeated the card subtitle, and on the support-areas card
          the provenance repeated it a third time, so each ranked list carried
          four paragraphs of explainer. The footnote still exists on the
          distribution for the trace drawer and the export. */}
      <CardFootnote>
        <ProvenanceNote
          provenance={distribution.provenance}
          suppressed={distribution.hiddenCount > 0}
        />
      </CardFootnote>
    </Card>
  );
}

export function NeedsPanel({ view }: { view: CohortView }) {
  const all = [
    view.supportAreas,
    view.industries,
    view.educationGoals,
    view.meetingRhythms,
  ];
  const everythingSuppressed = all.every((d) => d.allSuppressed);
  const where = `at ${view.institution.legalName}, ${view.windowLabel}`;

  return (
    <section className="e-sec e-print-page">
      <SectionHead
        id="mentee-goals"
        title={SECTION_TITLE["mentee-goals"]}
        lede={SECTION_LEDE["mentee-goals"]}
      />

      {everythingSuppressed ? (
        <Card>
          <CardHeader>
            <CardTitle sub="Support areas, industry interest, education goal and meeting rhythm.">
              What students need
            </CardTitle>
          </CardHeader>
          <CardBody>
            <StateCard
              variant="building"
              title="These breakdowns begin at ten students per group"
            >
              Your students have already answered all four of these questions in
              the app, and P3 holds the answers. None of the individual groups
              has reached ten students yet, so none is published. All four cards
              appear here together the moment the first group clears the
              threshold.
            </StateCard>
          </CardBody>
          <CardFootnote>
            <ProvenanceNote provenance={view.supportAreas.provenance} suppressed />
          </CardFootnote>
        </Card>
      ) : (
        <>
          <div className="e-grid">
            <DistributionCard
              distribution={view.supportAreas}
              title="What students are asking for"
              sub="Students pick one or more areas, so rows sum to selections."
              span={2}
              color={SERIES[0]}
              contextLabel={`Support areas students selected ${where}`}
            />
            <DistributionCard
              distribution={view.educationGoals}
              title="Where students are headed"
              sub="One education goal per student."
              solo
              color={SERIES[2]}
              contextLabel={`Education goals ${where}`}
            />
          </div>

          <div className="e-grid">
            <DistributionCard
              distribution={view.industries}
              title="Industries students are interested in"
              sub="One industry per student."
              span={2}
              color={SERIES[1]}
              contextLabel={`Industry interest ${where}`}
            />
            <DistributionCard
              distribution={view.meetingRhythms}
              title="How often students want to meet"
              sub="A stated preference your advising team can plan against."
              solo
              color={SERIES[3]}
              contextLabel={`Meeting rhythm preferences ${where}`}
            />
          </div>
        </>
      )}

    </section>
  );
}

